import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

import { Subject, Subscription, noop } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';

/**
 * How to use it:
 * <button
 *  TSDebounceClick
 *  (debounceClick)="doSomething()" (Function from parent component to be called)
 *  [debounceTime]="700"
 * >
 *  Save Something
 * </button>
 */

@Directive({
  selector: '[TSDebounceClick]',
})
export class TSDebounceClickDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() debounceTime: number = 300;
  @Input() allowBubbling: boolean = false;
  @Output() debounceClick: EventEmitter<unknown> = new EventEmitter();

  private clicks$: Subject<unknown> = new Subject();
  private subscription$: Subscription;

  ngOnInit() {
    this.subscription$ = this.clicks$
      .pipe(
        debounceTime(this.debounceTime),
        filter(() => !this.isHostDisabled()),
        tap((event) => {
          this.debounceClick.emit(event);
        }),
      )
      .subscribe(noop);
  }

  ngOnDestroy() {
    this.subscription$.unsubscribe();
  }

  @HostListener('click', ['$event'])
  clickEvent(event: MouseEvent) {
    if (this.isHostDisabled()) {
      return;
    }

    this.clicks$.next(event);

    if (!this.allowBubbling) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private isHostDisabled(): boolean {
    const element = this.elementRef.nativeElement;

    if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
      return element.disabled;
    }

    return element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true';
  }
}
